(ns admin.components.composite.activity-boost-workflow
  (:require
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [goog.object :as gobj]

   [kyber.styles.core :refer [gs styles->classes]]
   [kyber.components.composite.tab-bar :as tab-bar]
   [kyber.components.base.text :as text]
   [kyber.components.base.button :as button]
   [kyber.components.base.icons :as icon]))

(def ^:private max-container-width 800)

(def classes
  (styles->classes
   {:container
    {:max-width max-container-width
     :margin [(gs [:spacing :p12]) :auto
              (gs [:spacing :p20]) :auto]

     "& .tab-bar-tab-container button"
     {:box-shadow :none}}

    :boost-type-selection-container
    {:margin (gs [:spacing :p20])
     :display :flex
     :justify-content :space-between

     "& > *"
     {:width (str "calc(50% - " (gs [:spacing :p12]) ")")}}

    :segmentation-selection-container
    {:margin (gs [:spacing :p20])
     :display :flex
     :flex-direction :column
     :justify-content :space-between

     "& > *"
     {:width "100%"
      :margin-bottom (gs [:spacing :p20])
      :justify-content :flex-start}}

    :list
    {:list-style :decimal
     :margin-left (gs [:spacing :p20])
     :margin-bottom (gs [:spacing :p20])}

    :card-container
    {:border (str "1px solid " (gs [:colors :gray-scale-100]))
     :border-radius (gs [:radius :r3])
     :display :flex
     :flex-direction :column

     "&:hover"
     {:transition "border 0.4s ease-in-out"
      :border (str "1px solid " (gs [:colors :primary-lite-300]))}}

    :card-selected-container
    {:border (str "1px solid " (gs [:colors :primary-lite-500]) " !important")}

    :card-header
    {:background (gs [:colors :gray-scale-50])
     :padding [(gs [:spacing :p12])
               (gs [:spacing :p8])]
     :display :flex
     :justify-content :center
     :align-items :center}

    :card-title
    {:color (gs [:colors :gray-scale-600])
     :margin-right (gs [:spacing :p12])}

    :card-body-container
    {:margin [(gs [:spacing :p12])
              (gs [:spacing :p20])]
     :display :flex
     :justify-content :space-between
     :flex-direction :column}

    :segmentation-card-header
    {:background (gs [:colors :gray-scale-50])
     :padding [(gs [:spacing :p12])
               (gs [:spacing :p20])]
     :display :flex
     :justify-content :space-between
     :align-items :center}

    :segmentation-card-body-container
    {:margin [(gs [:spacing :p12])
              (gs [:spacing :p20])]
     :display :flex
     :justify-content :space-between}

    :type-body-container
    {:min-height "265px"}

    :type-button
    {:margin :auto
     :width "150px"}

    :step-one-navigation
    {:display :flex
     :justify-content :flex-end
     :width "100%"
     :margin-top (gs [:spacing :p20])}

    :step-two-navigation
    {:display :flex
     :justify-content :space-between
     :width "100%"
     :margin-top (gs [:spacing :p20])}

    :boost-name-container
    {:display :flex
     :flex-direction :column
     :justify-content :center
     :min-height "200px"
     :margin [(gs [:spacing :p40])
              (gs [:spacing :p64])]}}))

(def ^:private step-one-disabled? (constantly false))
(defn- step-two-disabled?
  [{:keys [boost-type]}]
  (nil? boost-type))
(defn- step-three-disabled?
  [{:keys [boost-type boost-name]}]
  (or (nil? boost-type)
      (empty? boost-name)))
(defn- save-and-build-disabled?
  [{:keys [boost-type boost-name segmentation]}]
  (or (nil? boost-type)
      (empty? boost-name)
      (nil? segmentation)))

(defn- render-type-selection-card
  [{:keys [boost-type-name value icon body form-state]}]
  (let [on-click (fn [_]
                   (swap! form-state assoc :boost-type value)
                   (tab-bar/update-url-query :boost-name))
        selected (= value (:boost-type @form-state))]
    [:div {:class (cond-> [(:card-container classes)]
                    (true? selected)
                    (conj (:card-selected-container classes)))}
     [:div {:class (:card-header classes)}
      [text/title-40 :h4 {:class (:card-title classes)} boost-type-name]
      [icon/icon-lg icon]]
     [:div {:class (:card-body-container classes)}
      body
      (if (true? selected)
        [button/primary-button
         {:class (:type-button classes)
          :on-click on-click
          :data-test (str boost-type-name "-select-button")} "Selected"]
        [button/secondary-button
         {:class (:type-button classes)
          :on-click on-click
          :data-test (str boost-type-name "-select-button")} "Select"])]]))

(defn- render-segmentation-selection-card
  [{:keys [segmentation-name value message form-state]}]
  (let [on-click #(swap! form-state assoc :segmentation value)
        selected (= value (:segmentation @form-state))]
    [:div {:class (cond-> [(:card-container classes)]
                    (true? selected)
                    (conj (:card-selected-container classes)))}
     [:div {:class (:segmentation-card-header classes)}
      [text/title-40 :h4 {:class (:card-title classes)} segmentation-name]]
     [:div {:class (:segmentation-card-body-container classes)}
      [text/caption-20 :p message]
      (if (true? selected)
        [button/primary-button
         {:style {:margin :initial}
          :class (:type-button classes)
          :on-click on-click} "Selected"]
        [button/secondary-button
         {:style {:margin :initial}
          :class (:type-button classes)
          :on-click on-click} "Select"])]]))

(defn- render-type-step
  [form-state]
  [:<>
   [text/body-30 :p
    {:style {:margin [(gs [:spacing :p40])
                      (gs [:spacing :p0])]}}
    (str "Please select one of two boost types. "
         "Click Select to begin building your boost.")]
   [:div {:class (:boost-type-selection-container classes)}
    [render-type-selection-card
     {:boost-type-name "Scheduled Boost"
      :value :scheduled
      :icon :calendar
      :form-state form-state
      :body [:div {:class (:type-body-container classes)}
             [text/body-20 :p
              {:style {:font-weight (gs [:font-weight :title-10])
                       :margin-bottom (gs [:spacing :p20])}}
              (str "A Scheduled Boost is designed to be delivered by email to "
                   "learners at a specific time after they have engaged with "
                   "the activity. You may choose to have a Schedule Boost sent "
                   "to")]

             [:ol {:class (:list classes)}
              [:li "All learners who started the activity"]
              [:li "All learners who completed the activity"]
              [:li (str "Only learners who started but haven't completed the "
                        "activity")]]

             [text/caption-20 :p
              (str "Keep in mind that you can author, save, and edit a Boost; "
                   "but it will not be scheduled (and sent) until you click to "
                   "publish the boost!")]]}]
    [render-type-selection-card
     {:boost-type-name "Immediate Boost"
      :value :immediate
      :icon :inbox-in
      :form-state form-state
      :body [:div {:class (:type-body-container classes)}
             [text/body-20 :p
              {:style {:font-weight (gs [:font-weight :title-10])
                       :margin-bottom (gs [:spacing :p20])}}
              (str "An Immediate Boost is designed to be delivered by email to "
                   "all learners immediately after it is published. You may "
                   "choose to have an Immediate Boost sent to; ")]

             [:ol {:class (:list classes)}
              [:li "All learners who started the activity"]
              [:li "All learners who completed the activity"]
              [:li (str "Only learners who started but haven't completed the "
                        "activity")]]

             [text/caption-20 :p
              (str "Keep in mind that you can author, save, and edit a Boost; "
                   "but it will not be sent until you click to publish the "
                   "boost!")]]}]]

   [:div {:class (:step-one-navigation classes)}
    [button/primary-button
     {:disabled (step-two-disabled? @form-state)
      :on-click #(tab-bar/update-url-query :boost-name)}
     "Boost Name >"]]])

(defn- render-name-step
  [form-state]

  (when (step-two-disabled? @form-state)
    (tab-bar/update-url-query :boost-type))

  [:<>
   [:div {:class (:boost-name-container classes)}
    [text/title-20 :p "Boost Name"]
    [text/caption-30 :p
     (str "The Boost Name is how the boost is labelled within the Admin "
          "environment. The Boost Name is NOT surfaced to learners, "
          "but allows you to easily identify the boost.")]
    [:input {:type :text
             :default-value (:boost-name @form-state)
             :on-change #(swap! form-state assoc :boost-name
                                (gobj/getValueByKeys % "target" "value"))}]]
   [:div {:class (:step-two-navigation classes)}
    [button/primary-button
     {:on-click #(tab-bar/update-url-query :boost-type)}
     "< Boost Type"]
    [button/primary-button
     {:disabled (step-three-disabled? @form-state)
      :on-click #(tab-bar/update-url-query :segmentation)}
     "Segmentation >"]]])

(defn- render-segmentation-step
  [form-state {:keys [activity-uuid curriculum-uuid]}]

  (when (step-two-disabled? @form-state)
    (tab-bar/update-url-query :boost-name))

  (let [is-submitting
        (re-frame/subscribe [:request/is-dispatching :boosts/create-boost])]
    (fn [form-state]
      (let [{:keys [boost-name boost-type segmentation]} @form-state]
        [:<>
         [text/body-30 :p
          {:style {:margin [(gs [:spacing :p40])
                            (gs [:spacing :p0])]}}
          "Please select which learners should receive this boost."]
         [:div {:class (:segmentation-selection-container classes)}
          [render-segmentation-selection-card
           {:segmentation-name "Started Activity"
            :message (str "Send this boost to all learners who have started "
                          "this activity!")
            :value :trigger-when-activity-started
            :form-state form-state}]
          [render-segmentation-selection-card
           {:segmentation-name "Completed Activity"
            :message (str "Send this boost to all learners who have completed "
                          "this activity!")
            :value :trigger-when-activity-completed
            :form-state form-state}]
          [render-segmentation-selection-card
           {:segmentation-name "Started, Not Completed"
            :message (str "Only send this boost to learners who have started "
                          "this activity, but have not completed it!")
            :value :started-not-completed
            :form-state form-state}]]

         [:div {:class (:step-two-navigation classes)}
          [button/primary-button
           {:on-click #(tab-bar/update-url-query :boost-name)}
           "< Boost Name"]
          [button/primary-button
           {:disabled (or (save-and-build-disabled? @form-state)
                          @is-submitting)
            :on-click #(re-frame/dispatch
                        [:boosts/create-boost
                         (cond->
                             {:name boost-name
                              :system-type boost-type
                              :show-banner true
                              :boost-type segmentation
                              :curriculum-uuid curriculum-uuid
                              :activity-uuid activity-uuid
                              :skip-when-activity-completed false}

                           ;; Remaps to the coorect boost-type and sets
                           ;; the skip flag
                           (= segmentation :started-not-completed)
                           (assoc :boost-type :trigger-when-activity-started
                                  :skip-when-activity-completed true))])}
           "Save & Build"]]]))))

(defn render
  [{:keys [params]}]
  (let [form-state (reagent/atom {})]

    ;; Ensure clean form on mount
    (reset! form-state {})

    (fn []
      [:div {:class (:container classes)}
       [tab-bar/render
        {:tabs [{:key :boost-type
                 :name "Boost Type"
                 :render [render-type-step form-state]
                 :disabled #(step-one-disabled? @form-state)}
                {:key :boost-name
                 :name "Boost Name"
                 :render [render-name-step form-state]
                 :disabled #(step-two-disabled? @form-state)}
                {:key :segmentation
                 :name "segmentation"
                 :render [render-segmentation-step form-state params]
                 :disabled #(step-three-disabled? @form-state)}]}]])))
